// packages/types/src/data-api.ts
// Single source of truth for the Data API search filter grammar and include
// whitelist. Each filterable field is a FieldDescriptor carrying its kind, a
// human description, and (for closed vocabularies) its enum values, sourced from
// the canonical @parcel/types vocabularies. apps/api builds its zod validator
// from these (enforcing enums) and apps/mcp builds its tool JSON Schema from
// them (advertising values). SQL lives in apps/api.

import {
  ACCOUNT_TYPE_DEFINITIONS,
  CONTACT_ROLE_DEFINITIONS,
  type EntityEnumDefinition,
  GC_STATUS_DEFINITIONS,
  PRIMARY_USE_DEFINITIONS,
  PROJECT_ACCOUNT_ROLE_DEFINITIONS,
  STORIES_KNOWN_DEFINITIONS,
} from './entity-vocabularies.js';
import { PROJECT_STAGE_DEFINITIONS } from './project-stages.js';
import { SIGNAL_TYPE_DEFINITIONS } from './signal-types.js';

export type FieldKind = 'enum' | 'text' | 'number' | 'date';

export interface FieldEnumValue {
  value: string;
  label: string;
  description: string;
}

export interface FieldDescriptor {
  key: string;
  kind: FieldKind;
  description: string;
  /** Present iff kind === 'enum'. Closed vocabulary from the canonical @parcel/types arrays. */
  values?: readonly FieldEnumValue[];
  /** In the API grammar but not the MCP tool schema, which has a byte budget. */
  omitFromMcp?: true;
}

// Cap for workspace batch write calls, shared by the API routes (envelope
// validation in apps/api/src/lib/batch.ts) and the MCP tool schemas
// (maxItems), so the advertised and enforced bounds cannot drift.
export const BATCH_MAX = 100;

export const enumField = (
  key: string,
  values: readonly FieldEnumValue[],
  description: string,
): FieldDescriptor => ({ key, kind: 'enum', description, values });

export const textField = (key: string, description: string): FieldDescriptor => ({
  key,
  kind: 'text',
  description,
});

export const numberField = (key: string, description: string): FieldDescriptor => ({
  key,
  kind: 'number',
  description,
});

export const dateField = (key: string, description: string): FieldDescriptor => ({
  key,
  kind: 'date',
  description,
});

// Project facts shared by the global and workspace descriptors, so the two cannot drift.
const PROJECT_FACT_FILTER_FIELDS: FieldDescriptor[] = [
  enumField(
    'gc_status',
    GC_STATUS_DEFINITIONS,
    'Contractor named, stated in a filing as not yet selected, or unknown (derived).',
  ),
  {
    ...enumField(
      'stories_known',
      STORIES_KNOWN_DEFINITIONS,
      'Whether a source states the storey count; unknown finds the projects a stories range never matches. Unknown does not mean one storey. REST API only; not in the MCP search tools.',
    ),
    omitFromMcp: true,
  },
];

const PROJECT_FACT_RANGE_FIELDS: FieldDescriptor[] = [
  numberField(
    'stories',
    'Storeys a source states. A range never matches a project with no stated count.',
  ),
];

/** Bare key list for code (tests, SQL drift guards) that only needs names. */
export const fieldKeys = (fields: readonly FieldDescriptor[]): string[] => fields.map((f) => f.key);

export interface RelationalDescriptor {
  filterFields: FieldDescriptor[];
  rangeFields: FieldDescriptor[];
  description: string; // REQUIRED: relational semantics are otherwise opaque to an MCP client.
}

export interface SearchSchemaDescriptor {
  filterFields: FieldDescriptor[];
  rangeFields: FieldDescriptor[];
  sortKeys: string[];
  queryDescription?: string;
  relational?: Record<string, RelationalDescriptor>;
}

export const CONTACT_QUERY_DESCRIPTION =
  'Fuzzy search over contact name, title, email, phone, and associated account name.';

export type DataEntitySingular = 'account' | 'contact' | 'project' | 'signal';
export type DataEntityPlural = 'accounts' | 'contacts' | 'projects' | 'signals';

// Shared relational inner field-sets, referenced by both the has_* and the
// without_* entries (and by the global and workspace descriptors) so the field
// lists are declared once. Only the per-block `description` differs.
const REL_PROJECT_ACCOUNT = {
  filterFields: [
    enumField('role', PROJECT_ACCOUNT_ROLE_DEFINITIONS, 'Role the account plays on the project.'),
    textField('name', 'Account name (fuzzy text match).'),
    enumField('account_type', ACCOUNT_TYPE_DEFINITIONS, 'Account classification.'),
  ],
  rangeFields: [],
};
const REL_SIGNAL = {
  filterFields: [enumField('signal_type', SIGNAL_TYPE_DEFINITIONS, 'Type of development signal.')],
  rangeFields: [dateField('filed_at', 'Filing date of the signal.')],
};
const REL_PROJECT_CONTACT = {
  filterFields: [
    enumField('role', CONTACT_ROLE_DEFINITIONS, 'Role the contact plays on the project.'),
    textField('title', 'Contact job title (fuzzy text match).'),
  ],
  rangeFields: [],
};
const REL_ACCOUNT_PROJECT = {
  filterFields: [
    enumField('stage', PROJECT_STAGE_DEFINITIONS, 'Canonical development stage of the project.'),
    enumField('primary_use', PRIMARY_USE_DEFINITIONS, 'Primary use / asset type of the project.'),
    textField('city', 'City the project is located in.'),
    textField('name', 'Project name (fuzzy text match).'),
  ],
  rangeFields: [numberField('residential_units', 'Number of residential units.')],
};
const REL_ACCOUNT_CONTACT = {
  filterFields: [
    textField('title', 'Contact job title (fuzzy text match).'),
    textField('name', 'Contact name (fuzzy text match).'),
  ],
  rangeFields: [],
};

export const SEARCH_SCHEMAS: Record<DataEntityPlural, SearchSchemaDescriptor> = {
  accounts: {
    filterFields: [
      enumField(
        'account_type',
        ACCOUNT_TYPE_DEFINITIONS,
        'Account classification (developer, architect, GC, etc.).',
      ),
      textField('city', 'City the account is located in.'),
      textField('state', 'Two-letter US state code.'),
      textField('name', 'Account name (fuzzy text match).'),
    ],
    rangeFields: [],
    // projects_count ranks the most active accounts in one call.
    sortKeys: ['name', 'created_at', 'projects_count'],
    relational: {
      has_project: {
        ...REL_ACCOUNT_PROJECT,
        description: 'Match accounts involved in a project matching these criteria.',
      },
      without_project: {
        ...REL_ACCOUNT_PROJECT,
        description: 'Match accounts NOT involved in any project matching these criteria.',
      },
      has_signal: {
        ...REL_SIGNAL,
        description:
          'Match accounts involved in a project that has a signal matching these criteria.',
      },
      without_signal: {
        ...REL_SIGNAL,
        description:
          'Match accounts NOT involved in any project that has a signal matching these criteria.',
      },
      has_contact: {
        ...REL_ACCOUNT_CONTACT,
        description: 'Match accounts that have a contact matching these criteria.',
      },
      without_contact: {
        ...REL_ACCOUNT_CONTACT,
        description: 'Match accounts that do NOT have any contact matching these criteria.',
      },
    },
  },
  contacts: {
    queryDescription: CONTACT_QUERY_DESCRIPTION,
    filterFields: [
      textField('name', 'Contact name (fuzzy text match).'),
      textField('title', 'Contact job title (fuzzy text match).'),
      textField('email', 'Contact email address.'),
      textField('account_id', 'Filter to contacts at a single account by its UUID.'),
    ],
    rangeFields: [],
    sortKeys: ['name', 'created_at', 'projects_count'],
    relational: {
      at_account: {
        filterFields: [
          textField('name', 'Account name (fuzzy text match).'),
          textField('city', 'City the account is located in.'),
          enumField('account_type', ACCOUNT_TYPE_DEFINITIONS, 'Account classification.'),
        ],
        rangeFields: [],
        description: 'Match contacts whose account matches these criteria.',
      },
      on_project: {
        filterFields: [
          enumField(
            'stage',
            PROJECT_STAGE_DEFINITIONS,
            'Canonical development stage of the project.',
          ),
          enumField(
            'primary_use',
            PRIMARY_USE_DEFINITIONS,
            'Primary use / asset type of the project.',
          ),
          textField('city', 'City the project is located in.'),
          enumField('role', CONTACT_ROLE_DEFINITIONS, 'Role the contact plays on the project.'),
          textField('name', 'Project name (fuzzy text match).'),
        ],
        rangeFields: [],
        description: 'Match contacts working on a project matching these criteria.',
      },
    },
  },
  projects: {
    filterFields: [
      enumField('stage', PROJECT_STAGE_DEFINITIONS, 'Canonical development stage of the project.'),
      enumField('primary_use', PRIMARY_USE_DEFINITIONS, 'Primary use / asset type of the project.'),
      textField('city', 'City the project is located in.'),
      textField('neighborhood', 'Neighborhood name; case and punctuation ignored (Boston only).'),
      textField('state', 'Two-letter US state code.'),
      textField('name', 'Project name (fuzzy text match).'),
      ...PROJECT_FACT_FILTER_FIELDS,
    ],
    rangeFields: [
      ...PROJECT_FACT_RANGE_FIELDS,
      numberField(
        'residential_units',
        'Number of residential units proposed. Extracted from filing documents; null if not stated.',
      ),
      numberField(
        'gross_floor_area_sf',
        'Gross floor area in square feet, as stated in the application or permit. Extracted from filings; null if not stated.',
      ),
      numberField(
        'cost_of_construction_usd',
        'Estimated total construction cost in USD, as declared in the permit application. Extracted from filings; null if not stated.',
      ),
      dateField(
        'last_signal_at',
        'The filed_at date of the most recent signal on this project. Use to filter for recently active projects.',
      ),
    ],
    sortKeys: ['last_signal_at', 'name', 'created_at', 'signals_count'],
    relational: {
      has_account: {
        ...REL_PROJECT_ACCOUNT,
        description: 'Match projects with an involved account matching these criteria.',
      },
      without_account: {
        ...REL_PROJECT_ACCOUNT,
        description:
          'Match projects that do NOT have any involved account matching these criteria.',
      },
      has_signal: {
        ...REL_SIGNAL,
        description: 'Match projects with a signal matching these criteria.',
      },
      without_signal: {
        ...REL_SIGNAL,
        description: 'Match projects that do NOT have any signal matching these criteria.',
      },
      has_contact: {
        ...REL_PROJECT_CONTACT,
        description: 'Match projects with a contact matching these criteria.',
      },
      without_contact: {
        ...REL_PROJECT_CONTACT,
        description: 'Match projects that do NOT have any contact matching these criteria.',
      },
    },
  },
  signals: {
    filterFields: [
      enumField('signal_type', SIGNAL_TYPE_DEFINITIONS, 'Type of development signal.'),
      textField('project_id', 'Filter to signals on a single project by its UUID.'),
    ],
    rangeFields: [
      dateField(
        'filed_at',
        'The event date: when the filing, permit, or action occurred, as stated in the source document. Sort and filter by this when you care about when something happened.',
      ),
      dateField(
        'discovered_at',
        'The date Parcel ingested and classified this signal. Typically lags filed_at by hours to days. Use for "recently added to Parcel" queries.',
      ),
    ],
    sortKeys: ['filed_at', 'discovered_at'],
    relational: {
      for_project: {
        filterFields: [
          enumField(
            'stage',
            PROJECT_STAGE_DEFINITIONS,
            'Canonical development stage of the project.',
          ),
          enumField(
            'primary_use',
            PRIMARY_USE_DEFINITIONS,
            'Primary use / asset type of the project.',
          ),
          textField('city', 'City the project is located in.'),
        ],
        rangeFields: [numberField('residential_units', 'Number of residential units.')],
        description: 'Match signals whose project matches these criteria.',
      },
      for_account: {
        filterFields: [
          textField('name', 'Account name (fuzzy text match).'),
          enumField('account_type', ACCOUNT_TYPE_DEFINITIONS, 'Account classification.'),
        ],
        rangeFields: [],
        description: 'Match signals whose project involves an account matching these criteria.',
      },
    },
  },
};

// The workspace book's sort keys and the relational grammar the old score criterion kinds still
// parse until Filters PR2; the public workspace searches filter with where instead.
export const WORKSPACE_SEARCH_SCHEMAS: Record<
  'accounts' | 'projects' | 'contacts',
  SearchSchemaDescriptor
> = {
  accounts: {
    filterFields: [
      enumField('account_type', ACCOUNT_TYPE_DEFINITIONS, 'Account classification.'),
      textField('city', 'City the account is located in.'),
      textField('state', 'Two-letter US state code.'),
      textField('name', 'Account name (fuzzy text match).'),
      textField(
        'workspace_status',
        'Free-form pipeline status label set by your team (e.g. "active prospect", "in conversation", "closed"). Not validated; whatever text your team uses.',
      ),
      textField(
        'tags',
        'A free-form label your team applied to this account. Filter matches any single tag value.',
      ),
    ],
    rangeFields: [],
    sortKeys: ['updated_at', 'icp_score', 'name', 'created_at', 'projects_count'],
    relational: {
      has_project: {
        ...REL_ACCOUNT_PROJECT,
        description: 'Match your accounts involved in a project matching these criteria.',
      },
      without_project: {
        ...REL_ACCOUNT_PROJECT,
        description: 'Match your accounts NOT involved in any project matching these criteria.',
      },
      has_signal: {
        ...REL_SIGNAL,
        description:
          'Match your accounts involved in a project that has a signal matching these criteria.',
      },
      without_signal: {
        ...REL_SIGNAL,
        description:
          'Match your accounts NOT involved in any project that has a signal matching these criteria.',
      },
      has_contact: {
        ...REL_ACCOUNT_CONTACT,
        description: 'Match your accounts that have a contact matching these criteria.',
      },
      without_contact: {
        ...REL_ACCOUNT_CONTACT,
        description: 'Match your accounts that do NOT have any contact matching these criteria.',
      },
    },
  },
  projects: {
    filterFields: [
      enumField('stage', PROJECT_STAGE_DEFINITIONS, 'Canonical development stage of the project.'),
      enumField('primary_use', PRIMARY_USE_DEFINITIONS, 'Primary use / asset type of the project.'),
      textField('city', 'City the project is located in.'),
      {
        ...textField(
          'neighborhood',
          'Neighborhood name; case and punctuation ignored (Boston only). REST API only; not in the MCP search tools.',
        ),
        omitFromMcp: true,
      },
      textField('state', 'Two-letter US state code.'),
      textField('name', 'Project name (fuzzy text match).'),
      textField(
        'workspace_status',
        'Free-form pipeline status label set by your team. Not validated; whatever text your team uses.',
      ),
      textField(
        'tags',
        'A free-form label your team applied to this project. Filter matches any single tag value.',
      ),
      ...PROJECT_FACT_FILTER_FIELDS,
    ],
    rangeFields: [
      ...PROJECT_FACT_RANGE_FIELDS,
      numberField(
        'residential_units',
        'Number of residential units proposed. Extracted from filing documents; null if not stated.',
      ),
      numberField(
        'gross_floor_area_sf',
        'Gross floor area in square feet, as stated in the application or permit. Extracted from filings; null if not stated.',
      ),
      numberField(
        'cost_of_construction_usd',
        'Estimated total construction cost in USD, as declared in the permit application. Extracted from filings; null if not stated.',
      ),
      dateField(
        'last_signal_at',
        'The filed_at date of the most recent signal on this project. Use to filter for recently active projects.',
      ),
    ],
    sortKeys: [
      'updated_at',
      'last_signal_at',
      'name',
      'created_at',
      'signals_count',
      'atlas_score',
    ],
    relational: {
      has_account: {
        ...REL_PROJECT_ACCOUNT,
        description: 'Match your projects with an involved account matching these criteria.',
      },
      without_account: {
        ...REL_PROJECT_ACCOUNT,
        description:
          'Match your projects that do NOT have any involved account matching these criteria.',
      },
      has_signal: {
        ...REL_SIGNAL,
        description: 'Match your projects with a signal matching these criteria.',
      },
      without_signal: {
        ...REL_SIGNAL,
        description: 'Match your projects that do NOT have any signal matching these criteria.',
      },
      has_contact: {
        ...REL_PROJECT_CONTACT,
        description: 'Match your projects with a contact matching these criteria.',
      },
      without_contact: {
        ...REL_PROJECT_CONTACT,
        description: 'Match your projects that do NOT have any contact matching these criteria.',
      },
    },
  },
  contacts: {
    queryDescription: CONTACT_QUERY_DESCRIPTION,
    filterFields: [
      textField('name', 'Contact name (fuzzy text match).'),
      textField('title', 'Contact job title (fuzzy text match).'),
      textField('email', 'Contact email address.'),
      textField('account_id', 'Filter to contacts at a single account by its UUID.'),
      textField(
        'workspace_status',
        'Free-form pipeline status label set by your team. Not validated; whatever text your team uses.',
      ),
      textField(
        'tags',
        'A free-form label your team applied to this contact. Filter matches any single tag value.',
      ),
    ],
    rangeFields: [],
    sortKeys: ['updated_at', 'name', 'created_at', 'projects_count', 'prospect_score'],
    relational: {
      at_account: {
        filterFields: [
          textField('name', 'Account name (fuzzy text match).'),
          textField('city', 'City the account is located in.'),
          enumField('account_type', ACCOUNT_TYPE_DEFINITIONS, 'Account classification.'),
        ],
        rangeFields: [],
        description: 'Match your contacts whose account matches these criteria.',
      },
      on_project: {
        filterFields: [
          enumField(
            'stage',
            PROJECT_STAGE_DEFINITIONS,
            'Canonical development stage of the project.',
          ),
          enumField(
            'primary_use',
            PRIMARY_USE_DEFINITIONS,
            'Primary use / asset type of the project.',
          ),
          textField('city', 'City the project is located in.'),
          enumField('role', CONTACT_ROLE_DEFINITIONS, 'Role the contact plays on the project.'),
          textField('name', 'Project name (fuzzy text match).'),
        ],
        rangeFields: [],
        description: 'Match your contacts working on a project matching these criteria.',
      },
    },
  },
};

export type IncludeRelationKind = 'one' | 'many';
export interface IncludeRelationSpec {
  kind: IncludeRelationKind;
}

// Valid include keys per entity (keyed by singular entity name, as the API
// dispatches includes by singular entity). 'one' unwraps to an object|null;
// 'many' returns an array. The SQL for each relation lives in apps/api include.ts.
// Typed with a string index (NOT Record<DataEntitySingular, ...>) so include.ts
// indexes it with its `entity: string` params without a cast, matching the
// existing const it replaces. The data-api test asserts all four are present.
export const INCLUDE_WHITELIST: Record<string, Record<string, IncludeRelationSpec>> = {
  account: {
    projects: { kind: 'many' },
    contacts: { kind: 'many' },
    signals: { kind: 'many' },
  },
  contact: {
    account: { kind: 'one' },
    projects: { kind: 'many' },
  },
  project: {
    accounts: { kind: 'many' },
    contacts: { kind: 'many' },
    signals: { kind: 'many' },
  },
  signal: {
    project: { kind: 'one' },
    accounts: { kind: 'many' },
  },
};
